/**
 * HUB-D — Données caisse pour onglet Centre Activité.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeCaisseKpis, matchesCaisseSearch } from '../../utils/caisse/caisseDisplay';

export default function useActiviteCaisse(api, filters, { enabled = true } = {}) {
  const [sessions, setSessions] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [openStatus, setOpenStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monthlyMonth, setMonthlyMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const fetchSessions = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.date_debut) params.append('date_debut', filters.date_debut);
    if (filters.date_fin) params.append('date_fin', filters.date_fin);
    if (filters.magasin_id) params.append('magasin_id', filters.magasin_id);
    const response = await api.get(`/caisse/historique?${params.toString()}`);
    return response.data || [];
  }, [api, filters.date_debut, filters.date_fin, filters.magasin_id]);

  const fetchMonthly = useCallback(async (month = monthlyMonth) => {
    const params = { month };
    if (filters.magasin_id) params.magasin_id = filters.magasin_id;
    const response = await api.get('/caisse/monthly', { params });
    return response.data?.rows || [];
  }, [api, filters.magasin_id, monthlyMonth]);

  const fetchOpenStatus = useCallback(async () => {
    try {
      const params = filters.magasin_id ? { magasin_id: filters.magasin_id } : {};
      const response = await api.get('/caisse/status', { params });
      return response.data;
    } catch {
      return null;
    }
  }, [api, filters.magasin_id]);

  const refresh = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const [sess, status] = await Promise.all([fetchSessions(), fetchOpenStatus()]);
      setSessions(sess);
      setOpenStatus(status?.is_open ? status : null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement caisse');
      setSessions([]);
      setOpenStatus(null);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, fetchSessions, fetchOpenStatus]);

  const refreshMonthly = useCallback(async (month) => {
    if (!api || !enabled) return;
    setMonthlyLoading(true);
    try {
      const rows = await fetchMonthly(month ?? monthlyMonth);
      setMonthlyRows(rows);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement mensuel');
      setMonthlyRows([]);
    } finally {
      setMonthlyLoading(false);
    }
  }, [api, enabled, fetchMonthly, monthlyMonth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    refreshMonthly(monthlyMonth);
  }, [refreshMonthly, monthlyMonth]);

  const filteredSessions = useMemo(
    () => sessions.filter((s) => matchesCaisseSearch(s, filters.search)),
    [sessions, filters.search],
  );

  const filteredMonthly = useMemo(
    () => monthlyRows.filter((r) => matchesCaisseSearch(r, filters.search)),
    [monthlyRows, filters.search],
  );

  const kpis = useMemo(
    () => computeCaisseKpis(filteredSessions, filteredMonthly),
    [filteredSessions, filteredMonthly],
  );

  return {
    sessions: filteredSessions,
    monthlyRows: filteredMonthly,
    openStatus,
    kpis,
    loading,
    monthlyLoading,
    error,
    monthlyMonth,
    setMonthlyMonth,
    refresh,
    refreshMonthly,
  };
}
